import { useState, useEffect, useRef } from 'react';
import { Upload, X, Check, AlertCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const DESIGN_TYPES = [
  'print-tech',
  'data-texture',
  'cinematic',
  'glitch',
  'classical',
  'illustrated',
  'minimal',
  'playful',
  'art-deco',
  'brutalist',
  'other',
];

const NICHES = ['ecommerce', 'blog', 'factory', 'machinery', 'fintech', 'skincare', 'shops', 'other'];

const CATEGORIES = ['website', 'app'];

const SOURCE_TYPES = ['url', 'youtube', 'pinterest', 'other'];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const inputClasses = (isDark) =>
  `w-full px-4 py-2 rounded-lg border transition-colors duration-fast outline-none focus:ring-2 ${
    isDark
      ? 'bg-background-dark border-border-dark text-white placeholder-gray-600 focus:border-primary-dark focus:ring-primary-dark/30'
      : 'bg-white border-border text-text placeholder-text-muted focus:border-primary focus:ring-primary/20'
  }`;

const checkboxClasses = (isDark) =>
  `w-4 h-4 rounded accent-current transition-transform duration-fast active:scale-90 ${
    isDark ? 'text-primary-dark' : 'text-primary'
  }`;

export default function AddInspirationForm() {
  const { isDark } = useTheme();
  const [token, setToken] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Form state
  const [title, setTitle] = useState('');
  const [designTypes, setDesignTypes] = useState([]);
  const [customDesignTypes, setCustomDesignTypes] = useState('');
  const [niches, setNiches] = useState([]);
  const [customNiches, setCustomNiches] = useState('');
  const [category, setCategory] = useState('website');
  const [source, setSource] = useState('');
  const [sourceType, setSourceType] = useState('url');
  const [images, setImages] = useState([]);
  const [hasAnimation, setHasAnimation] = useState(false);
  const [animationNotes, setAnimationNotes] = useState('');
  const [quickNotes, setQuickNotes] = useState('');
  const [brief, setBrief] = useState('');
  const [tags, setTags] = useState('');

  const objectUrlsRef = useRef(new Set());

  // Revoke every blob URL we ever created when the component unmounts
  useEffect(() => {
    const urls = objectUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const handleTokenSubmit = async (e) => {
    e.preventDefault();
    if (!token.trim()) return;

    setAuthLoading(true);
    setMessage(null);

    try {
      const response = await fetch('/api/library/validate-token', {
        method: 'POST',
        headers: { 'X-Admin-Token': token },
      });

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(
          `Server didn't return JSON (status ${response.status}). The /api routes need \`vercel dev\` or a deployed environment - plain \`npm run dev\` doesn't run them.`
        );
      }

      if (response.ok) {
        setIsAuthenticated(true);
      } else {
        setMessage({ type: 'error', text: 'Incorrect token.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const next = files.map((file) => {
      const previewUrl = URL.createObjectURL(file);
      objectUrlsRef.current.add(previewUrl);
      return {
        file,
        name: file.name,
        type: file.type,
        previewUrl,
        brief: '',
        backgroundRecipe: '',
        detailsOpen: false,
      };
    });
    setImages((prev) => [...prev, ...next]);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages((prev) => {
      const target = prev[index];
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
        objectUrlsRef.current.delete(target.previewUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const updateImageField = (index, field, value) => {
    setImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, [field]: value } : img))
    );
  };

  const toggleImageDetails = (index) => {
    setImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, detailsOpen: !img.detailsOpen } : img))
    );
  };

  const toggleDesignType = (type) => {
    setDesignTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const toggleNiche = (niche) => {
    setNiches((prev) =>
      prev.includes(niche) ? prev.filter((n) => n !== niche) : [...prev, niche]
    );
  };

  const resetForm = () => {
    images.forEach((img) => {
      URL.revokeObjectURL(img.previewUrl);
      objectUrlsRef.current.delete(img.previewUrl);
    });
    setTitle('');
    setDesignTypes([]);
    setCustomDesignTypes('');
    setNiches([]);
    setCustomNiches('');
    setCategory('website');
    setSource('');
    setImages([]);
    setHasAnimation(false);
    setAnimationNotes('');
    setQuickNotes('');
    setBrief('');
    setTags('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const tagList = tags
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);

      const customNicheList = customNiches
        .split(',')
        .map((n) => n.trim().toLowerCase())
        .filter((n) => n.length > 0);

      const customDesignTypeList = customDesignTypes
        .split(',')
        .map((d) => d.trim().toLowerCase())
        .filter((d) => d.length > 0);

      const finalNiches = [...niches.filter((n) => n !== 'other'), ...customNicheList];
      const finalDesignTypes = [
        ...designTypes.filter((d) => d !== 'other'),
        ...customDesignTypeList,
      ];

      const encodedImages = await Promise.all(
        images.map(async (img) => ({
          name: img.name,
          type: img.type,
          buffer: await fileToBase64(img.file),
          ...(img.brief.trim() && { brief: img.brief.trim() }),
          ...(img.backgroundRecipe.trim() && { backgroundRecipe: img.backgroundRecipe.trim() }),
        }))
      );

      const payload = {
        title,
        designTypes: finalDesignTypes,
        niches: finalNiches,
        category,
        source: sourceType === 'url' ? source : `${sourceType}: ${source}`,
        images: encodedImages,
        hasAnimation,
        animationNotes: hasAnimation ? animationNotes : '',
        quickNotes,
        brief,
        tags: tagList,
        token,
      };

      const response = await fetch('/api/library/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': token,
        },
        body: JSON.stringify(payload),
      });

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(
          `Server didn't return JSON (status ${response.status}). The /api routes need \`vercel dev\` or a deployed environment - plain \`npm run dev\` doesn't run them.`
        );
      }

      const result = await response.json();

      if (response.ok) {
        setMessage({
          type: 'success',
          text: `"${title}" added to the library.`,
        });
        resetForm();
      } else {
        setMessage({
          type: 'error',
          text: result.errors?.[0] || result.error || 'Failed to add inspiration',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div
        className={`min-h-[100dvh] flex items-center justify-center px-4 ${
          isDark ? 'bg-background-dark' : 'bg-background'
        }`}
      >
        <div
          className={`w-full max-w-md p-8 rounded-lg border ${
            isDark
              ? 'bg-background-dark border-border-dark'
              : 'bg-white border-border'
          }`}
        >
          <h2
            className={`text-2xl font-display font-semibold mb-6 ${
              isDark ? 'text-white' : 'text-text'
            }`}
          >
            Admin Access
          </h2>

          {message && (
            <div
              className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
                isDark
                  ? 'bg-red-900/20 border border-red-800 text-red-400'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}
            >
              <AlertCircle size={16} className="shrink-0" />
              {message.text}
            </div>
          )}

          <form onSubmit={handleTokenSubmit}>
            <input
              type="password"
              placeholder="Enter admin token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className={`${inputClasses(isDark)} mb-4`}
              autoFocus
            />

            <button
              type="submit"
              disabled={authLoading}
              className={`w-full py-2 rounded-lg font-medium transition-all duration-base ease-smooth flex items-center justify-center gap-2 ${
                authLoading
                  ? 'opacity-60 cursor-not-allowed'
                  : 'active:scale-[0.97]'
              } ${
                isDark
                  ? 'bg-primary-dark text-background-dark hover:bg-primary-dark/90'
                  : 'bg-primary text-white hover:bg-primary-hover'
              }`}
            >
              {authLoading && <Loader2 size={16} className="animate-spin" />}
              {authLoading ? 'Checking...' : 'Enter Library'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-[100dvh] px-4 py-8 ${isDark ? 'bg-background-dark' : 'bg-background'}`}>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1
            className={`text-4xl font-display font-semibold ${
              isDark ? 'text-white' : 'text-text'
            }`}
          >
            Add Inspiration
          </h1>
          <button
            type="button"
            onClick={() => setIsAuthenticated(false)}
            className={`px-4 py-2 rounded-lg text-sm transition-all duration-fast active:scale-[0.97] ${
              isDark
                ? 'bg-border-dark text-gray-400 hover:text-white'
                : 'bg-border/30 text-text-muted hover:text-text'
            }`}
          >
            Logout
          </button>
        </div>

        {message && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 transition-opacity duration-base ${
              message.type === 'success'
                ? isDark
                  ? 'bg-green-900/20 border border-green-800 text-green-400'
                  : 'bg-green-50 border border-green-200 text-green-800'
                : isDark
                  ? 'bg-red-900/20 border border-red-800 text-red-400'
                  : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            {message.type === 'success' ? (
              <Check size={20} className="shrink-0" />
            ) : (
              <AlertCircle size={20} className="shrink-0" />
            )}
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className={`space-y-6 p-6 rounded-lg border ${
            isDark
              ? 'bg-background-dark/50 border-border-dark'
              : 'bg-white border-border'
          }`}
        >
          {/* Title */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-text'}`}>
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., SPADE Dashboard"
              className={inputClasses(isDark)}
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-text'}`}>
              Category *
            </label>
            <div className="flex gap-4">
              {CATEGORIES.map((cat) => (
                <label key={cat} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value={cat}
                    checked={category === cat}
                    onChange={(e) => setCategory(e.target.value)}
                    className={checkboxClasses(isDark)}
                  />
                  <span className={isDark ? 'text-gray-300' : 'text-text'}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Design Types */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-text'}`}>
              Design Types * (select multiple)
            </label>
            <div className="grid grid-cols-2 gap-2">
              {DESIGN_TYPES.map((type) => (
                <label key={type} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={designTypes.includes(type)}
                    onChange={() => toggleDesignType(type)}
                    className={checkboxClasses(isDark)}
                  />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-text'}`}>
                    {type}
                  </span>
                </label>
              ))}
            </div>

            {designTypes.includes('other') && (
              <input
                type="text"
                value={customDesignTypes}
                onChange={(e) => setCustomDesignTypes(e.target.value)}
                placeholder="Custom design types, comma-separated e.g. brutalist-editorial, retro-futurism"
                className={`${inputClasses(isDark)} mt-3`}
                required
              />
            )}
          </div>

          {/* Niches */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-text'}`}>
              Niches * (select multiple)
            </label>
            <div className="grid grid-cols-2 gap-2">
              {NICHES.map((niche) => (
                <label key={niche} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={niches.includes(niche)}
                    onChange={() => toggleNiche(niche)}
                    className={checkboxClasses(isDark)}
                  />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-text'}`}>
                    {niche}
                  </span>
                </label>
              ))}
            </div>

            {niches.includes('other') && (
              <input
                type="text"
                value={customNiches}
                onChange={(e) => setCustomNiches(e.target.value)}
                placeholder="Custom niches, comma-separated e.g. real-estate, wellness"
                className={`${inputClasses(isDark)} mt-3`}
                required
              />
            )}
          </div>

          {/* Source */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-text'}`}>
              Source *
            </label>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {SOURCE_TYPES.map((type) => (
                <label key={type} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value={type}
                    checked={sourceType === type}
                    onChange={(e) => setSourceType(e.target.value)}
                    className={checkboxClasses(isDark)}
                  />
                  <span className={`text-sm capitalize ${isDark ? 'text-gray-300' : 'text-text'}`}>
                    {type === 'url' ? 'URL' : type}
                  </span>
                </label>
              ))}
            </div>
            <input
              type="text"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder={
                sourceType === 'url'
                  ? 'https://example.com'
                  : `${sourceType.charAt(0).toUpperCase() + sourceType.slice(1)} source description`
              }
              className={`${inputClasses(isDark)} mt-2`}
              required
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-text'}`}>
              Images * (drag and drop or click)
            </label>
            <label
              className={`block p-6 rounded-lg border-2 border-dashed cursor-pointer text-center transition-colors duration-base ${
                isDark
                  ? 'border-border-dark hover:bg-border-dark/50'
                  : 'border-border hover:bg-background-sidebar'
              }`}
            >
              <Upload size={24} className="mx-auto mb-2" />
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-text-muted'}`}>
                Drop images here or click to select
              </div>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>

            {images.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {images.map((img, idx) => (
                  <div
                    key={img.previewUrl}
                    className={`relative rounded-lg border overflow-hidden ${
                      isDark ? 'border-border-dark' : 'border-border'
                    }`}
                  >
                    <div className="relative group">
                      <img
                        src={img.previewUrl}
                        alt={img.name}
                        className="w-full h-32 object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 p-1 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-fast active:scale-90"
                        aria-label={`Remove ${img.name}`}
                      >
                        <X size={14} className="text-white" />
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => toggleImageDetails(idx)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-xs font-medium transition-colors duration-fast ${
                        isDark
                          ? 'text-gray-400 hover:text-white bg-background-dark'
                          : 'text-text-muted hover:text-text bg-background-sidebar'
                      }`}
                    >
                      Image details (optional)
                      {img.detailsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>

                    {img.detailsOpen && (
                      <div className="p-3 space-y-2">
                        <textarea
                          value={img.brief}
                          onChange={(e) => updateImageField(idx, 'brief', e.target.value)}
                          placeholder="Brief for this image (what's happening here)"
                          rows={2}
                          className={`${inputClasses(isDark)} text-xs`}
                        />
                        <textarea
                          value={img.backgroundRecipe}
                          onChange={(e) => updateImageField(idx, 'backgroundRecipe', e.target.value)}
                          placeholder="Background recipe (for hero / specific sections only) e.g. fill [SUBJECT], muted palette, isolated on white ground"
                          rows={2}
                          className={`${inputClasses(isDark)} text-xs`}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Brief */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-text'}`}>
              Brief (optional, AI-copiable)
            </label>
            <textarea
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="A structured, AI-ready summary of this design (usually written after running it through an AI classifier). This is what gets copied for future AI reference."
              rows={4}
              className={inputClasses(isDark)}
            />
          </div>

          {/* Animation */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={hasAnimation}
                onChange={(e) => setHasAnimation(e.target.checked)}
                className={checkboxClasses(isDark)}
              />
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-text'}`}>
                Has Animation / Interaction
              </span>
            </label>

            {hasAnimation && (
              <textarea
                value={animationNotes}
                onChange={(e) => setAnimationNotes(e.target.value)}
                placeholder="Describe the animations you like: timing, easing, trigger, etc."
                rows={4}
                className={inputClasses(isDark)}
                required={hasAnimation}
              />
            )}
          </div>

          {/* Quick Notes */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-text'}`}>
              Quick Notes (what you like)
            </label>
            <textarea
              value={quickNotes}
              onChange={(e) => setQuickNotes(e.target.value)}
              placeholder="Why does this inspire you?"
              rows={3}
              className={inputClasses(isDark)}
            />
          </div>

          {/* Tags */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-text'}`}>
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="e.g., parallax, hero, fade-in, sophisticated"
              className={inputClasses(isDark)}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 rounded-lg font-medium transition-all duration-base ease-smooth flex items-center justify-center gap-2 ${
              loading
                ? 'opacity-60 cursor-not-allowed'
                : `active:scale-[0.98] ${
                    isDark
                      ? 'bg-primary-dark text-background-dark hover:bg-primary-dark/90'
                      : 'bg-primary text-white hover:bg-primary-hover'
                  }`
            } ${loading ? (isDark ? 'bg-primary-dark text-background-dark' : 'bg-primary text-white') : ''}`}
          >
            {loading && <Loader2 size={18} className="animate-spin" />}
            {loading ? 'Uploading...' : 'Add to Library'}
          </button>
        </form>
      </div>
    </div>
  );
}
